import {
  ProfileValueOption,
  ProfileFields,
  StoryDocument,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { ResolverFunction } from './resolvers.config';
import { FieldConfig, ProfilePageConfig } from '../types/profile.types';
import { createFieldConfig, DOCUMENT_CONFIGS } from '../utils/fields.helpers';
import {
  mapStandardProfileArrayValues,
  mapStandardProfileSingleValue,
} from '../utils/resolvers.helpers';

export const PROFILE_FIELD_CONFIGS: Partial<FieldConfig> = {
  demographicsGenders: createFieldConfig({
    key: 'demographicsGenders',
    type: { variant: 'SINGLE_SELECT' },
    width: 'full',
    placeholder: 'Select Gender',
  }),
  demographicsEthnicities: createFieldConfig({
    key: 'demographicsEthnicities',
    type: { variant: 'SINGLE_SELECT' },
    width: 'full',
    placeholder: 'Select Ethnicity',
  }),
  demographicsAffinities: createFieldConfig({
    key: 'demographicsAffinities',
    type: { variant: 'CHECKBOX_GROUP' },
  }),
  profileImage: createFieldConfig({
    key: 'profileImage',
    type: { variant: 'PROFILE_IMAGE' },
  }),
  profileBannerImage: createFieldConfig({
    key: 'profileBannerImage',
    type: { variant: 'PROFILE_IMAGE' },
  }),
  linkedInData: createFieldConfig({
    key: 'linkedInData',
    type: { variant: 'TEXT' },
    textLeadingAdornment: {
      name: 'LinkedIn',
    },
    placeholder: 'linkedin.com/in/your-handle-here',
    validation: {
      type: 'text',
      pattern:
        /^https?:\/\/(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+(?:-[a-zA-Z0-9]+)?\/?(?:\?.*)?$/i,
    },
  }),
  calendlyData: createFieldConfig({
    key: 'calendlyData',
    type: { variant: 'TEXT' },
    textLeadingAdornment: {
      name: 'Calendly',
    },
    placeholder: 'calendly.com/your-handle-here',
    validation: {
      type: 'text',
      pattern: /^https?:\/\/(?:www\.)?calendly\.com\/[a-zA-Z0-9-]+(?:\/\d+(?:min|hr))?(?:\?.*)?$/i,
    },
  }),
  documentsPurposeVideo: createFieldConfig({
    key: 'documentsPurposeVideo',
    type: { variant: 'DOCUMENT', fileUploadConfig: DOCUMENT_CONFIGS.purpose_video },
  }),
  user_bio: createFieldConfig({
    key: 'user_bio',
    type: { variant: 'TEXT', multiline: true, rows: 4 },
    width: 'full',
    placeholder: 'Tell us about yourself...',
  }),
};

export const PROFILE_PAGE_CONFIG: ProfilePageConfig[] = [
  {
    title: 'Public Profile',
    description: 'Update your photo and details here.',
    sections: [
      {
        title: 'Your photo',
        keys: ['profileImage'],
        description: 'This will be displayed on your profile',
      },
      {
        title: 'Your banner',
        keys: ['profileBannerImage'],
        description: 'Add a banner for a little self-expression.',
      },
      {
        title: 'Intro Video',
        keys: ['documentsPurposeVideo'],
        description: 'Record a short video explaining who you are and what brought you to Backrs.',
      },
      {
        title: 'About Me',
        keys: ['user_bio'],
        description: 'Share a little about yourself.',
        coachOnly: true,
      },
      {
        title: 'LinkedIn',
        keys: ['linkedInData'],
        description: 'This helps us know more about your professional & academic background.',
      },
      {
        title: 'Calendar',
        keys: ['calendlyData'],
        description: 'This will be displayed on your profile',
      },
    ],
  },
  {
    title: 'Private Profile',
    description: 'We use this information to add you to networks.',
    sections: [
      {
        title: 'Gender',
        keys: ['demographicsGenders'],
      },
      {
        title: 'Ethnicity',
        keys: ['demographicsEthnicities'],
        hideDivider: true,
      },
      {
        title: 'Affinity',
        keys: ['demographicsAffinities'],
        description:
          'Please check all that apply to you. This information helps us build connections within the Backrs community.',
      },
    ],
  },
];

export const PROFILE_HELPERS: Partial<
  Record<ProfileFields, ResolverFunction<string | number | ProfileValueOption | StoryDocument>>
> = {
  demographicsGenders: (profile, options) =>
    mapStandardProfileSingleValue(profile.profile.demographicsGenders, options.demographicsGenders),

  demographicsEthnicities: (profile, options) =>
    mapStandardProfileSingleValue(
      profile.profile.demographicsEthnicities,
      options.demographicsEthnicities,
    ),

  demographicsAffinities: (profile, options) =>
    mapStandardProfileArrayValues(
      profile.profile.demographicsAffinities,
      options.demographicsAffinities,
    ),

  linkedInData: (profile) => {
    const linkedInUrl = profile.profile.linkedInData?.linkedIn_url?.[0]?.value;

    return linkedInUrl ? [linkedInUrl] : [];
  },

  calendlyData: (profile) => {
    const calendlyUrl = profile.profile.calendlyData?.calendly_url?.[0]?.value;

    return calendlyUrl ? [calendlyUrl] : [];
  },

  documentsPurposeVideo: (profile) =>
    profile.profile.documentsPurposeVideo?.map((video) => {
      return {
        ...video,
        media_url: `${GROW_URL}/profile/${profile.profile.publicProfile.id}`,
      };
    }) || [],

  user_bio: (profile) => {
    return profile.profile.user_bio ? [profile.profile.user_bio] : [];
  },
};
